/**
 * BulkActionBar.jsx
 * Appears when one or more KPI rows are selected.
 * Shows count and a dropdown of bulk actions.
 *
 * Props:
 *   count        {number}    — selected item count
 *   onAction     {fn(key)}   — called with action key
 *   onDeselect   {fn}        — clear selection
 */

import React, { useState, useRef, useEffect } from "react";
import { KpiIcon } from "./KpiIcons";
import { SecondaryButton } from "../../../../globalComponents/ui/Button";

const BULK_ACTIONS = [
  { key: "assign", icon: "assign", label: "Assign Data Owner" },
  { key: "reassign", icon: "export", label: "Re Assign" },
  { key: "auditAssign", icon: "audit", label: "Assign Auditor" },
  { key: "prefill", icon: "copy", label: "Prefill From Previous Year" },
  { key: "reminder", icon: "alert", label: "Email Reminder" },
];


const BulkActionBar = ({ count = 0, onAction, onDeselect, hasPermission = () => true, showPrefill = true, disabledActions = [], disabledReasons = {} }) => {
  const [open, setOpen] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64);
  const dropRef = useRef(null);
  const sentinelRef = useRef(null);


  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // measure the app header so the pinned bar can match its height
  useEffect(() => {
    const nav = document.querySelector(".topbar_esg");
    if (nav) setHeaderHeight(Math.round(nav.getBoundingClientRect().height));
  }, []);

  // sentinel scrolled out of view ⇒ the sticky bar is pinned to the top
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting));
    obs.observe(el);
    return () => obs.disconnect();
  }, [count === 0]);

  if (count === 0) return null;

  return (
    <>
    <div ref={sentinelRef} style={{ height: 1, marginBottom: -1 }} />
    <div
      className="kpi-bulk-bar"
      style={{
        background: "var(--ink)",
        padding: "10px 28px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        position: "sticky",
        top: 0,
        zIndex: 300,
        minHeight: stuck ? headerHeight : undefined,
        boxShadow: stuck ? "0 4px 14px rgba(0,0,0,0.22)" : "none",
        transition: "min-height .15s ease, box-shadow .15s ease",
      }}
    >
      {/* Count + info */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 24, height: 24, borderRadius: "50%",
            background: "var(--accent)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 800, flexShrink: 0,
          }}
        >
          {count}
        </div>
        <span style={{ color: "#fff", fontWeight: 600, fontSize: 13, fontFamily: "var(--font-sans)" }}>
          item{count !== 1 ? "s" : ""} selected
        </span>
      </div>

      <div style={{ flex: 1 }} />

      {/* Actions dropdown */}
      <div ref={dropRef} style={{ position: "relative" }}>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 16px",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            border: "1.5px solid rgba(255,255,255,0.2)",
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 12.5,
            fontFamily: "var(--font-sans)",
            cursor: "pointer",
            letterSpacing: "0.02em",
            transition: "all 0.15s",
          }}
        >
          Bulk Actions
          <KpiIcon name="chevron" size={12} color="#fff" />
        </button>

        {open && (
          <div className="kpi-dropdown-menu" style={{ minWidth: 220 }}>
            {BULK_ACTIONS.filter(a => {
              if (a.key === "assign") return hasPermission("data-owner.assign") || hasPermission("data-owner.remove");
              if (a.key === "reassign") return hasPermission("data-owner.reassign");
              if (a.key === "auditAssign") return hasPermission("auditor.assign");
              if (a.key === "prefill") return showPrefill && hasPermission("prefill-from-previous-year.execute");
              if (a.key === "reminder") return hasPermission("email-reminder.send");
              return true;
            }).map((a) => {
              const disabled = disabledActions.includes(a.key);
              return (
                <div
                  key={a.key}
                  className={`kpi-dropdown-item${a.danger ? " danger" : ""}`}
                  onClick={() => {
                    if (disabled) return;
                    onAction?.(a.key);
                    setOpen(false);
                  }}
                  style={disabled ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                  title={disabled ? disabledReasons[a.key] : undefined}
                >
                  <KpiIcon name={a.icon} size={13} color="var(--ink-3)" />
                  <span>{a.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Deselect */}
      <SecondaryButton
        onClick={onDeselect}
        style={{
          background: "rgba(255,255,255,0.1)",
          color: "rgba(255,255,255,0.7)",
          border: "none",
          height: 32,
          fontSize: 12,
        }}
      >
        Deselect All
      </SecondaryButton>
    </div>
    </>
  );
};

export default BulkActionBar;